var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import debounce from "debounce-promise";
import { colors } from "healthex-common/colors";
import { IdentifiedPatientLastActivityType, IdentifiedPatientState, RequestConsentError, } from "healthex-common/consent.dtos";
import { defaultPageSize } from "healthex-common/constants";
import { ApiPaginationInput } from "healthex-common/result";
import { ProjectType, StudyAccessData, StudyPeriodicUnitType, StudyStatus } from "healthex-common/study.dtos";
import { enumValues, pluralize, uniqueArray, unslugifyOptional, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { FormModel } from "./FormModel";
import { validators } from "../Logic/Validators";
import { format } from "date-fns";
import { saveAs } from "../Logic/FileUtils";
import projectManagerEmptyImage from "../Media/project-manager-empty.png";
import projectManagerEmptyDeidentified from "../Media/project-manager-empty-deidentified.png";
import healthExIllustration3 from "../Media/HealthEx-illustration-3.png";
import { Disposer } from "../Infra/Disposer";
export class RequestConsentSuccessDialogModel extends SimpleDialogModel {
    constructor() {
        super(...arguments);
        this.errorPatients = [];
        this.tryAgainPatients = [];
        this.numberOfPatients = 0;
        this.dataAccessType = "PRESCREEN";
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], RequestConsentSuccessDialogModel.prototype, "errorPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], RequestConsentSuccessDialogModel.prototype, "tryAgainPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], RequestConsentSuccessDialogModel.prototype, "numberOfPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], RequestConsentSuccessDialogModel.prototype, "dataAccessType", void 0);
export class RequestConsentPendingDialogModel extends SimpleDialogModel {
    constructor() {
        super(...arguments);
        this.numberOfPatients = 0;
        this.dataAccessType = "PRESCREEN";
    }
}
__decorate([
    observable,
    __metadata("design:type", Number)
], RequestConsentPendingDialogModel.prototype, "numberOfPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], RequestConsentPendingDialogModel.prototype, "dataAccessType", void 0);
export var RecruitmentSelectAllBannerType;
(function (RecruitmentSelectAllBannerType) {
    RecruitmentSelectAllBannerType["SELECT_ALL"] = "SELECT_ALL";
    RecruitmentSelectAllBannerType["CLEAR_ALL"] = "CLEAR_ALL";
})(RecruitmentSelectAllBannerType || (RecruitmentSelectAllBannerType = {}));
export class RecruitmentViewModel {
    constructor(container) {
        this.studyVersions = NOT_LOADED;
        this.identifiedPatientSummary = NOT_LOADED;
        this.identifiedPatients = [];
        this.identifiedPatientIds = new Set();
        this.searchInput = { filter: "ALL" };
        this.allSelectedPatientIds = null;
        this.allSelectedPatients = null;
        this.sort = { field: "stage", sort: "asc" };
        this.totalIdentifiedPatients = 0;
        this.paginationState = { page: 0, pageSize: defaultPageSize };
        this.identifiedPatientsResponse = NOT_LOADED;
        this.requestConsentPendingDialogModel = new RequestConsentPendingDialogModel();
        this.requestConsentSuccessDialogModel = new RequestConsentSuccessDialogModel();
        this.shareDialogModel = new SimpleDialogModel();
        this.manageRemindersDialogModel = new SimpleDialogModel();
        this.dataRequestorAddPatientsDialogModel = new SimpleDialogModel();
        this.dataOriginatorAddPatientsDialogModel = new SimpleDialogModel();
        this.revocationDetailsDialogModel = new SimpleDialogModel();
        this.selectedPatientForRevocationDetailsDialog = null;
        this.disponser = new Disposer();
        this.updatePatientSearchTextInput = debounce(this._updatePatientSearchTextInput, 500);
        this.patientActions = {
            SEND_PRESCREEN_REQUEST: {
                label: (notificationsSuppressed) => notificationsSuppressed ? "Mark as requested to prescreen" : "Send prescreen request",
                action: this.sendPrescreenRequest.bind(this),
                group: 0,
            },
            RESEND_PRESCREEN_REQUEST: {
                label: "Send prescreen request reminder",
                action: this.resendPrescreenRequest.bind(this),
                group: 0,
            },
            REENGAGE_PRESCREEN_REQUEST: {
                label: "Re-engage and resend prescreening request",
                action: this.reengagePrescreenRequest.bind(this),
                group: 0,
            },
            SEND_ENROLLMENT_REQUEST: {
                label: (notificationsSuppressed) => (notificationsSuppressed ? "Mark as requested" : "Send request"),
                action: this.sendEnrollmentRequest.bind(this),
                group: 0,
            },
            RESEND_ENROLLMENT_REQUEST: {
                label: () => "Send request reminder",
                action: this.resendEnrollmentRequest.bind(this),
                group: 0,
            },
            REENGAGE_ENROLLMENT_REQUEST: {
                label: "Re-engage and resend request",
                action: this.reengageEnrollmentRequest.bind(this),
                group: 0,
            },
            UPDATE_HEALTH_RECORDS: {
                label: "Update health records",
                action: this.updateHealthRecords.bind(this),
                group: 0,
            },
            MARK_AS_CONTACTED: { label: "Mark as contacted", action: this.markAsContacted.bind(this), group: 1 },
            DOWNLOAD_DEIDENTIFIED_RECORDS: {
                label: "Download de-identified records",
                action: this.accessDeidentifiedData.bind(this),
                group: 2,
            },
            DOWNLOAD_PRESCREEN_RECORDS: {
                label: "Download identified records for prescreening",
                action: this.accessPrescreenData.bind(this),
                group: 2,
            },
            DOWNLOAD_ENROLLMENT_RECORDS: {
                label: (research) => (research ? "Download identified records for enrollment" : "Download identified records"),
                action: this.accessEnrollmentData.bind(this),
                group: 2,
            },
            REMOVE: { label: "Remove", action: this.removeFromCohort.bind(this), group: 3 },
            RESTORE: { label: "Restore", action: this.addBackToCohort.bind(this), group: 3 },
        };
        this.perPageSelectedPatientIds = new Map();
        this.selectAll = false;
        this.selectAllIdentifiedPatients = null;
        this.selectAllSelectedPatientsIds = null;
        this.numberOfCurrentPageIdentifiedPatients = 0;
        this.studyReminderDefaults = {
            enrollmentEnabled: false,
            enrollmentEvery: 7,
            enrollmentUnit: StudyPeriodicUnitType.DAY,
            enrollmentLimit: 5,
            prescreenEnabled: false,
            prescreenEvery: 7,
            prescreenUnit: StudyPeriodicUnitType.DAY,
            prescreenLimit: 5,
        };
        this.manageRemindersDialogFormModel = new FormModel(this.studyReminderDefaults, []);
        this.studyRecruitmentManager = container.studyRecruitmentManager;
        this.studyManager = container.studyManager;
        this.fullRecordService = container.fullRecordService;
        this.studiesUIManager = container.studiesUIManager;
        this.routingUIManager = container.routingUIManager;
        this.bannerUIManager = container.bannerUIManager;
        this.manageRemindersDialogFormModel.onInputUpdate = () => this.validateManageRemindersForm();
    }
    run() {
        this.disponser.reaction(() => [this.studyToManage], () => {
            void this.loadStudyVersions();
            void this.loadPatientSummary();
            if (this.studyToManage !== NOT_LOADED && this.studyToManage) {
                void this.studyRecruitmentManager.loadRecruitmentForStudy(this.studyToManage.id);
            }
        }, { fireImmediately: true });
        this.disponser.reaction(() => [this.routingUIManager.currentRouteParams], async () => {
            this.setInitialSearchFilter();
        }, { fireImmediately: true });
        this.disponser.reaction(() => [this.studyToManage, this.searchInput], () => {
            void this.loadPatients(this.searchInput.filter);
        }, { fireImmediately: true });
    }
    stop() {
        this.disponser.dispose();
    }
    get studyToManage() {
        return this.studiesUIManager.studyToManage;
    }
    get isStudyPaused() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.paused === true;
    }
    get isGeneralDataUse() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.projectType === ProjectType.GENERAL_DATA_USE;
    }
    get showCheckboxSelection() {
        return !this.isStudyPaused && !this.readonly;
    }
    get showStudyVersions() {
        return this.studyVersions !== NOT_LOADED && this.studyVersions.length > 1;
    }
    get showPatientActions() {
        return !this.isStudyPaused && !this.readonly;
    }
    get showBulkActions() {
        return !this.isStudyPaused && !this.readonly;
    }
    get showAddPatientsButton() {
        return !this.isStudyPaused && !this.readonly;
    }
    get showManagerRemindersButton() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return !this.readonly && !this.studyToManage.suppressNotifications;
    }
    get readonly() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.status === StudyStatus.COMPLETED || this.studyToManage.status === StudyStatus.WITHDRAWN;
    }
    get isPdda() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.pdda;
    }
    get noPatientInfo() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return null;
        const totalPatients = this.getNumberInFilter("ALL");
        if (totalPatients === null || totalPatients > 0)
            return null;
        if (this.studyToManage.status === StudyStatus.COMPLETED) {
            return {
                title: "No patients were added",
                text: "The project was completed before patients would be added.",
                image: healthExIllustration3,
            };
        }
        else if (this.studyToManage.status === StudyStatus.WITHDRAWN) {
            return {
                title: "No patients were added",
                text: "The project was withdrawn before patients would be added.",
                image: healthExIllustration3,
            };
        }
        else if (this.studyToManage.paused) {
            return {
                title: "No patients have been added yet",
                text: "Resume this project to add patients.",
                image: healthExIllustration3,
            };
        }
        else if (this.studyToManage.accessData === StudyAccessData.FULL_RECORD_DEIDENTIFIED) {
            return {
                title: "Add patients to access de-identified records",
                text: "You can add patients to access their de-identified health records. \nOnce added, you’ll be able to track analytics, such as their demographic data.",
                image: projectManagerEmptyDeidentified,
                showAddPatientsButton: true,
            };
        }
        else {
            return {
                title: "Add patients to request consent for access to their records",
                text: "You can add patients to this project and send them a request to confirm access to their health records.\nOnce added, you’ll be able to see their status, track progress, and manage their experience.",
                image: projectManagerEmptyImage,
                showAddPatientsButton: true,
            };
        }
    }
    get filters() {
        var _a;
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return [];
        const consentNeeded = ((_a = this.studyToManage) === null || _a === void 0 ? void 0 : _a.accessData) === StudyAccessData.FULL_RECORD_IDENTIFIED;
        return enumValues(IdentifiedPatientState).filter((state) => {
            if (state === IdentifiedPatientState.INFORMATION_SENT) {
                return false;
            }
            else if (state === IdentifiedPatientState.PRESCREEN_CONSENT_PENDING) {
                return false;
            }
            else if (state === IdentifiedPatientState.PRESCREEN_CONSENTED) {
                return false;
            }
            else if (state === IdentifiedPatientState.ENROLLED) {
                return consentNeeded;
            }
            else if (state === IdentifiedPatientState.ENROLLING) {
                return consentNeeded;
            }
            else if (state === IdentifiedPatientState.ENROLLING_INTERESTED) {
                return false;
            }
            else if (state === IdentifiedPatientState.ENROLLING_CONTACTED) {
                return false;
            }
            else if (state === IdentifiedPatientState.DECLINED_PRESCREEN) {
                return false;
            }
            else if (state === IdentifiedPatientState.DECLINED_ENROLLMENT) {
                return consentNeeded;
            }
            else if (state === IdentifiedPatientState.REVOKED_PRESCREEN) {
                return false;
            }
            else if (state === IdentifiedPatientState.REVOKED_ENROLLMENT) {
                return consentNeeded;
            }
            else if (state === IdentifiedPatientState.REMOVED) {
                return true;
            }
            return willNeverHappen(state);
        });
    }
    async loadStudyVersions() {
        if (!this.studyToManage || this.studyToManage === NOT_LOADED)
            return;
        this.studyVersions = await this.studyManager.getStudyVersions(this.studyToManage.id);
    }
    async loadPatientSummary() {
        if (this.studyToManage === NOT_LOADED) {
            this.identifiedPatients = null;
            return;
        }
        if (this.studyToManage === null) {
            this.identifiedPatients = [];
            return;
        }
        this.identifiedPatientSummary = await this.studyRecruitmentManager.getIdentifiedPatientSummary(this.studyToManage.id);
    }
    async loadPatients(filter) {
        var _a, _b, _c, _d, _e;
        if (this.studyToManage === NOT_LOADED) {
            this.identifiedPatients = null;
            return;
        }
        if (this.studyToManage === null) {
            this.identifiedPatients = [];
            return;
        }
        const { patientInfo } = this.searchInput;
        const paginationParams = (_a = this.paginationState) !== null && _a !== void 0 ? _a : { page: 0, pageSize: defaultPageSize };
        const searchInput = {
            patientInfo,
            state: filter === "ALL" ? undefined : [filter],
            start: paginationParams.page * paginationParams.pageSize,
            end: paginationParams.page * paginationParams.pageSize + paginationParams.pageSize,
            showPatientRequestConsentErrors: true,
        };
        this.identifiedPatientsResponse = await this.studyRecruitmentManager.getIdentifiedPatients(this.studyToManage.id, searchInput);
        const suppressNotifications = this.studyToManage.suppressNotifications;
        this.identifiedPatients =
            (_c = (_b = this.identifiedPatientsResponse) === null || _b === void 0 ? void 0 : _b.patients.map((patient) => ({
                ...patient,
                lastActivityText: this.getLastActivityText(patient, suppressNotifications),
            }))) !== null && _c !== void 0 ? _c : [];
        this.identifiedPatientIds = this.identifiedPatients
            ? new Set(this.identifiedPatients.map((identifiedPatient) => identifiedPatient.patientId))
            : new Set();
        this.totalIdentifiedPatients = (_e = (_d = this.identifiedPatientsResponse) === null || _d === void 0 ? void 0 : _d.total) !== null && _e !== void 0 ? _e : 0;
        this.numberOfCurrentPageIdentifiedPatients = this.identifiedPatientIds.size;
        const selectAllIdentifiedPatients = this.selectAllIdentifiedPatients;
        const allSelectedPatientIds = this.allSelectedPatientIds;
        if (selectAllIdentifiedPatients) {
            await this.loadSelectAllPatients(this.searchInput.filter);
        }
        else if (allSelectedPatientIds) {
            this.updateSelectedPatientIds(this.searchInput.filter, allSelectedPatientIds);
        }
        if (selectAllIdentifiedPatients) {
            this.selectAllIdentifiedPatients = [...selectAllIdentifiedPatients];
        }
    }
    get noPatientsFoundWithSearch() {
        var _a, _b;
        const isFiltered = ((_b = (_a = this.searchInput.patientInfo) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 || this.searchInput.filter !== "ALL";
        return isFiltered && this.identifiedPatients && this.identifiedPatients.length === 0;
    }
    get selectedPatients() {
        var _a, _b;
        if (this.selectAll) {
            return (_a = this.selectAllIdentifiedPatients) !== null && _a !== void 0 ? _a : [];
        }
        return (_b = this.allSelectedPatients) !== null && _b !== void 0 ? _b : [];
    }
    getLastActivityText(patient, suppressNotifications) {
        if (!patient.lastActivityType || !patient.lastActivityDate)
            return "";
        const lastActivityConfig = {
            INFORMATION_SENT: [],
            PRESCREEN_CONSENT_PENDING: [
                IdentifiedPatientLastActivityType.PRESCREENING_REQUEST_SENT,
                IdentifiedPatientLastActivityType.REMINDER_PRESCREENING_REQUEST_SENT,
                IdentifiedPatientLastActivityType.REENGAED_PRESCREENING_REQUEST_SENT,
            ],
            PRESCREEN_CONSENTED: [IdentifiedPatientLastActivityType.CONSENTED_TO_PRESCREENING],
            ENROLLING: [
                IdentifiedPatientLastActivityType.ENROLLMENT_REQUEST_SENT,
                IdentifiedPatientLastActivityType.REMINDER_ENROLLMENT_REQUEST_SENT,
                IdentifiedPatientLastActivityType.REENGAED_ENROLLMENT_REQUEST_SENT,
            ],
            ENROLLING_INTERESTED: [],
            ENROLLING_CONTACTED: [IdentifiedPatientLastActivityType.CONTACTED_BY_RESEARCHER],
            ENROLLED: [
                IdentifiedPatientLastActivityType.CONSENTED_TO_ENROLLMENT,
                IdentifiedPatientLastActivityType.HEALTH_RECORD_UPDATED,
                IdentifiedPatientLastActivityType.HEALTH_RECORD_UPDATE_REQUEST_SENT,
            ],
            DECLINED_PRESCREEN: [IdentifiedPatientLastActivityType.DECLINED_PRESCREENING],
            DECLINED_ENROLLMENT: [IdentifiedPatientLastActivityType.DECLINED_ENROLLMENT],
            REVOKED_PRESCREEN: [IdentifiedPatientLastActivityType.REVOKED_PRESCREENING],
            REVOKED_ENROLLMENT: [IdentifiedPatientLastActivityType.REVOKED_ENROLLMENT],
            REMOVED: [],
        };
        if (lastActivityConfig[patient.state].includes(patient.lastActivityType)) {
            const date = format(new Date(patient.lastActivityDate), "LLL dd, yyyy");
            return `${this.getLastActivityTextFromType(patient.lastActivityType, suppressNotifications)} ${date}`;
        }
        return "";
    }
    getLastActivityTextFromType(type, suppressNotifications) {
        switch (type) {
            case IdentifiedPatientLastActivityType.PRESCREENING_REQUEST_SENT:
                return suppressNotifications ? "Marked as requested to prescreen on" : "Prescreening request sent on";
            case IdentifiedPatientLastActivityType.ENROLLMENT_REQUEST_SENT:
                return suppressNotifications ? "Marked as requested to enroll on" : "Enrollment request sent on";
            case IdentifiedPatientLastActivityType.REMINDER_PRESCREENING_REQUEST_SENT:
                return "Prescreening request reminder sent on";
            case IdentifiedPatientLastActivityType.REMINDER_ENROLLMENT_REQUEST_SENT:
                return "Enrollment request reminder sent on";
            case IdentifiedPatientLastActivityType.REENGAED_PRESCREENING_REQUEST_SENT:
                return "Re-engaged prescreening request re-sent on";
            case IdentifiedPatientLastActivityType.REENGAED_ENROLLMENT_REQUEST_SENT:
                return "Re-engaged enrollment request re-sent on";
            case IdentifiedPatientLastActivityType.CONSENTED_TO_PRESCREENING:
                return "Consented to prescreening on";
            case IdentifiedPatientLastActivityType.CONSENTED_TO_ENROLLMENT:
                return "Consented to enrollment on";
            case IdentifiedPatientLastActivityType.DECLINED_PRESCREENING:
                return "Declined prescreening on";
            case IdentifiedPatientLastActivityType.DECLINED_ENROLLMENT:
                return "Declined enrollment on";
            case IdentifiedPatientLastActivityType.REVOKED_PRESCREENING:
                return "Declined prescreening on";
            case IdentifiedPatientLastActivityType.REVOKED_ENROLLMENT:
                return "Revoked enrollment consent on";
            case IdentifiedPatientLastActivityType.CONTACTED_BY_RESEARCHER:
                return "Contacted by researcher on";
            case IdentifiedPatientLastActivityType.HEALTH_RECORD_UPDATED:
                return "Records updated on";
            case IdentifiedPatientLastActivityType.HEALTH_RECORD_UPDATE_REQUEST_SENT:
                return suppressNotifications ? "Marked as needing health record update on" : "Health record update request sent on";
        }
        return willNeverHappen(type);
    }
    setInitialSearchFilter() {
        var _a, _b, _c;
        this.searchInput.filter = (_c = unslugifyOptional((_b = (_a = this.routingUIManager) === null || _a === void 0 ? void 0 : _a.currentRouteParams) === null || _b === void 0 ? void 0 : _b.filter)) !== null && _c !== void 0 ? _c : "ALL";
        this.paginationState = { page: this.currentPaginationPage, pageSize: defaultPageSize };
    }
    updateSearchInput(update) {
        if ("filter" in update) {
            this.studiesUIManager.setCurrentStudyManagerTab("PATIENT_MANAGEMENT", update.filter);
        }
        else {
            this.searchInput = { ...this.searchInput, ...update };
        }
        if (update.filter) {
            if (update.filter === "ALL") {
                this.updateSort({ field: "stage", sort: "asc" });
            }
            else {
                this.updateSort(undefined);
            }
        }
    }
    updateSort(sortModel) {
        this.sort = sortModel;
    }
    refreshPatientData() {
        this.updateSearchInput({ ...this.searchInput });
    }
    get searchPlaceholderText() {
        return this.isPdda
            ? "Search for patients by name, ID, or contact information. Add multiple values using commas."
            : "Search for patients by MRN or ID. Add multiple values using commas.";
    }
    _updatePatientSearchTextInput(input) {
        const patientInfo = input.split(/(?:[ ]+)?,(?:[ ]+)?/);
        this.updateSearchInput({ patientInfo });
    }
    getNumberInFilter(filter) {
        if (this.identifiedPatientSummary === NOT_LOADED || !this.identifiedPatientSummary)
            return null;
        const identifiedPatientSummary = this.identifiedPatientSummary;
        return (filter === "ALL" ? enumValues(IdentifiedPatientState) : [filter])
            .map((state) => { var _a; return (_a = identifiedPatientSummary[state]) !== null && _a !== void 0 ? _a : 0; })
            .reduce((a, b) => a + b, 0);
    }
    getStatusDescription(state) {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return "";
        if (this.studyToManage.projectType === ProjectType.GENERAL_DATA_USE) {
            switch (state) {
                case IdentifiedPatientState.ENROLLED:
                    return "Consented";
                case IdentifiedPatientState.ENROLLING:
                    return "Requested";
                case IdentifiedPatientState.DECLINED_ENROLLMENT:
                    return "Declined";
                case IdentifiedPatientState.REVOKED_ENROLLMENT:
                    return "Revoked consent";
                case IdentifiedPatientState.REMOVED:
                    return "Removed";
                case IdentifiedPatientState.INFORMATION_SENT:
                case IdentifiedPatientState.PRESCREEN_CONSENT_PENDING:
                case IdentifiedPatientState.PRESCREEN_CONSENTED:
                case IdentifiedPatientState.ENROLLING_CONTACTED:
                case IdentifiedPatientState.ENROLLING_INTERESTED:
                case IdentifiedPatientState.DECLINED_PRESCREEN:
                case IdentifiedPatientState.REVOKED_PRESCREEN:
                    return "Unknown";
            }
            return willNeverHappen(state);
        }
        return willNeverHappen(this.studyToManage.projectType);
    }
    getStatusColor(state) {
        switch (state) {
            case IdentifiedPatientState.INFORMATION_SENT:
                return colors.chips_ember;
            case IdentifiedPatientState.PRESCREEN_CONSENT_PENDING:
                return colors.chips_neptune;
            case IdentifiedPatientState.PRESCREEN_CONSENTED:
                return colors.chips_amethyst;
            case IdentifiedPatientState.ENROLLED:
                return colors.chips_forest;
            case IdentifiedPatientState.ENROLLING:
                return colors.chips_azure;
            case IdentifiedPatientState.ENROLLING_INTERESTED:
                return colors.chips_ember;
            case IdentifiedPatientState.ENROLLING_CONTACTED:
                return colors.chips_saffron;
            case IdentifiedPatientState.DECLINED_PRESCREEN:
            case IdentifiedPatientState.REVOKED_PRESCREEN:
                return colors.chips_tulip;
            case IdentifiedPatientState.DECLINED_ENROLLMENT:
            case IdentifiedPatientState.REVOKED_ENROLLMENT:
                return colors.chips_cherry;
            case IdentifiedPatientState.REMOVED:
                return colors.chips_slate;
        }
        return willNeverHappen(state);
    }
    compareStates(a, b) {
        return enumValues(IdentifiedPatientState).indexOf(a) - enumValues(IdentifiedPatientState).indexOf(b);
    }
    get showMRN() {
        return !this.isPdda;
    }
    get showNameAndContactInformation() {
        return this.isPdda;
    }
    get isStudyUsingIdentifiedData() {
        return this.studiesUIManager.isStudyUsingIdentifiedData;
    }
    get bulkActionButtonText() {
        var _a;
        const numberOfSelectedPatients = ((_a = this.getSelectedPatientIds()) !== null && _a !== void 0 ? _a : []).length;
        return `Bulk action (${numberOfSelectedPatients})`;
    }
    getPatientActionLabel(action) {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return "";
        if (typeof action.label === "string") {
            return action.label;
        }
        return action.label(this.studyToManage.suppressNotifications);
    }
    getPatientActions(patient) {
        const actions = this.getPatientActionsForState(patient.state);
        return actions.filter((action) => {
            const hasDataAccess = patient.dataDeliveryAllowed;
            if (action === this.patientActions.DOWNLOAD_DEIDENTIFIED_RECORDS) {
                return hasDataAccess;
            }
            else if (action === this.patientActions.DOWNLOAD_PRESCREEN_RECORDS) {
                return patient.hasPrescreenConsented && hasDataAccess;
            }
            else if (action === this.patientActions.DOWNLOAD_ENROLLMENT_RECORDS) {
                return hasDataAccess;
            }
            else if (action === this.patientActions.SEND_PRESCREEN_REQUEST || action === this.patientActions.RESEND_PRESCREEN_REQUEST) {
                return !patient.prescreeningRequestNotAllowedReason;
            }
            else if (action === this.patientActions.SEND_ENROLLMENT_REQUEST || action === this.patientActions.RESEND_ENROLLMENT_REQUEST) {
                return !patient.enrollmentRequestNotAllowedReason;
            }
            else if (action === this.patientActions.REENGAGE_PRESCREEN_REQUEST) {
                return !!patient.prescreeningRequestNotAllowedReason;
            }
            else if (action === this.patientActions.REENGAGE_ENROLLMENT_REQUEST) {
                return !!patient.enrollmentRequestNotAllowedReason;
            }
            else if (action === this.patientActions.MARK_AS_CONTACTED) {
                return !!patient.interested && !patient.contacted;
            }
            else if (action === this.patientActions.UPDATE_HEALTH_RECORDS) {
                return this.isGeneralDataUse && patient.state === IdentifiedPatientState.ENROLLED;
            }
            return true;
        });
    }
    getPatientActionsForState(patientState) {
        var _a, _b, _c;
        if (this.readonly)
            return [];
        if (this.studyToManage === NOT_LOADED)
            return [];
        const actions = this._getPatientActionsForState(patientState);
        const requestsAllowed = ((_a = this.studyToManage) === null || _a === void 0 ? void 0 : _a.accessData) === StudyAccessData.FULL_RECORD_IDENTIFIED;
        const rerequestsAllowed = requestsAllowed && !((_b = this.studyToManage) === null || _b === void 0 ? void 0 : _b.suppressNotifications);
        const dataDelivery = !this.isPdda;
        const dataDeliveryIdentified = dataDelivery && ((_c = this.studyToManage) === null || _c === void 0 ? void 0 : _c.accessData) === StudyAccessData.FULL_RECORD_IDENTIFIED;
        const enrolled = patientState === IdentifiedPatientState.ENROLLED;
        return actions.filter((action) => {
            if (action === this.patientActions.DOWNLOAD_DEIDENTIFIED_RECORDS) {
                return dataDelivery;
            }
            else if (action === this.patientActions.DOWNLOAD_PRESCREEN_RECORDS) {
                return false;
            }
            else if (action === this.patientActions.DOWNLOAD_ENROLLMENT_RECORDS) {
                return dataDeliveryIdentified;
            }
            else if (action === this.patientActions.SEND_PRESCREEN_REQUEST) {
                return false;
            }
            else if (action === this.patientActions.RESEND_PRESCREEN_REQUEST ||
                action === this.patientActions.REENGAGE_PRESCREEN_REQUEST) {
                return false;
            }
            else if (action === this.patientActions.SEND_ENROLLMENT_REQUEST) {
                return false;
            }
            else if (action === this.patientActions.RESEND_ENROLLMENT_REQUEST ||
                action === this.patientActions.REENGAGE_ENROLLMENT_REQUEST) {
                return rerequestsAllowed;
            }
            else if (action === this.patientActions.MARK_AS_CONTACTED) {
                return false;
            }
            else if (action === this.patientActions.UPDATE_HEALTH_RECORDS) {
                return enrolled && this.isGeneralDataUse;
            }
            return true;
        });
    }
    _getPatientActionsForState(patientState) {
        switch (patientState) {
            case IdentifiedPatientState.PRESCREEN_CONSENT_PENDING:
                return [
                    this.patientActions.RESEND_PRESCREEN_REQUEST,
                    this.patientActions.DOWNLOAD_DEIDENTIFIED_RECORDS,
                    this.patientActions.REMOVE,
                ];
            case IdentifiedPatientState.PRESCREEN_CONSENTED:
                return [
                    this.patientActions.SEND_ENROLLMENT_REQUEST,
                    this.patientActions.DOWNLOAD_PRESCREEN_RECORDS,
                    this.patientActions.REMOVE,
                ];
            case IdentifiedPatientState.ENROLLING:
                return [
                    this.patientActions.RESEND_ENROLLMENT_REQUEST,
                    this.patientActions.DOWNLOAD_PRESCREEN_RECORDS,
                    this.patientActions.REMOVE,
                ];
            case IdentifiedPatientState.ENROLLING_INTERESTED:
                return [this.patientActions.SEND_ENROLLMENT_REQUEST, this.patientActions.MARK_AS_CONTACTED, this.patientActions.REMOVE];
            case IdentifiedPatientState.ENROLLING_CONTACTED:
                return [
                    this.patientActions.SEND_ENROLLMENT_REQUEST,
                    this.patientActions.DOWNLOAD_PRESCREEN_RECORDS,
                    this.patientActions.REMOVE,
                ];
            case IdentifiedPatientState.ENROLLED:
                return [
                    this.patientActions.DOWNLOAD_ENROLLMENT_RECORDS,
                    this.patientActions.UPDATE_HEALTH_RECORDS,
                    this.patientActions.REMOVE,
                ];
            case IdentifiedPatientState.DECLINED_PRESCREEN:
            case IdentifiedPatientState.REVOKED_PRESCREEN:
                return [this.patientActions.REENGAGE_PRESCREEN_REQUEST, this.patientActions.REMOVE];
            case IdentifiedPatientState.DECLINED_ENROLLMENT:
            case IdentifiedPatientState.REVOKED_ENROLLMENT:
                return [this.patientActions.REENGAGE_ENROLLMENT_REQUEST, this.patientActions.REMOVE];
            case IdentifiedPatientState.REMOVED:
                return [this.patientActions.RESTORE];
            case IdentifiedPatientState.INFORMATION_SENT:
                return [
                    this.patientActions.SEND_PRESCREEN_REQUEST,
                    this.patientActions.SEND_ENROLLMENT_REQUEST,
                    this.patientActions.DOWNLOAD_DEIDENTIFIED_RECORDS,
                    this.patientActions.REMOVE,
                ];
        }
        return willNeverHappen(patientState);
    }
    get bulkActions() {
        if (this.readonly)
            return null;
        if (!this.selectedPatients.length)
            return null;
        if (this.isStudyPaused)
            return null;
        const filter = this.searchInput.filter;
        const actionSetsForFilteredStates = (filter === "ALL" ? enumValues(IdentifiedPatientState) : [filter]).map((state) => {
            return new Set(this.getPatientActionsForState(state));
        });
        const possibleActions = Object.values(this.patientActions).filter((action) => {
            return actionSetsForFilteredStates.some((set) => set.has(action));
        });
        const actionSetsForPatients = this.selectedPatients.map((patient) => {
            return new Set(this.getPatientActions(patient));
        });
        return possibleActions.map((action) => {
            return {
                label: action.label,
                action: async () => {
                    var _a;
                    await action.action(this.selectedPatients, (_a = this.selectAll) !== null && _a !== void 0 ? _a : false);
                },
                enabled: actionSetsForPatients.every((set) => set.has(action)),
                group: action.group,
            };
        });
    }
    async sendPrescreenRequest(patients, isSelectAllBulkAction, isResend, isReengage) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const filter = this.searchInput.filter;
        try {
            this.requestConsentPendingDialogModel.numberOfPatients = patients.length;
            this.requestConsentPendingDialogModel.dataAccessType = "PRESCREEN";
            this.requestConsentPendingDialogModel.show();
            const result = await this.studyManager.requestPatientStudyConsentForIdentification(study, patients);
            this.requestConsentSuccessDialogModel.numberOfPatients = result.numberOfPatients;
            this.requestConsentSuccessDialogModel.errorPatients = result.errorPatients;
            this.requestConsentSuccessDialogModel.tryAgainPatients = result.errorPatients.filter((patient) => patient.error === RequestConsentError.INTERNAL_ERROR);
            this.requestConsentSuccessDialogModel.dataAccessType = "PRESCREEN";
            await Promise.all(result.successPatients.map(async (patient) => {
                await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                    lastActivityType: isResend
                        ? IdentifiedPatientLastActivityType.REMINDER_PRESCREENING_REQUEST_SENT
                        : isReengage
                            ? IdentifiedPatientLastActivityType.REENGAED_PRESCREENING_REQUEST_SENT
                            : IdentifiedPatientLastActivityType.PRESCREENING_REQUEST_SENT,
                });
            }));
        }
        finally {
            this.requestConsentPendingDialogModel.open = false;
            const confirmed = await this.requestConsentSuccessDialogModel.showAndWait();
            await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
            if (this.requestConsentSuccessDialogModel.numberOfPatients > 0) {
                this.displayBanner(this.requestConsentSuccessDialogModel.numberOfPatients, "to Prescreening");
            }
            if (!confirmed && this.requestConsentSuccessDialogModel.tryAgainPatients.length > 0) {
                const tryAgainPatients = patients.filter((patient) => this.requestConsentSuccessDialogModel.tryAgainPatients.some((errorPatient) => errorPatient.patientId === patient.patientId));
                await this.sendPrescreenRequest(tryAgainPatients, isSelectAllBulkAction, isResend, isReengage);
            }
        }
    }
    async resendPrescreenRequest(patients, isSelectAllBulkAction) {
        await this.sendPrescreenRequest(patients, isSelectAllBulkAction, true);
    }
    async reengagePrescreenRequest(patients, isSelectAllBulkAction) {
        await this.sendPrescreenRequest(patients, isSelectAllBulkAction, false, true);
    }
    displayBanner(patientCount, text) {
        this.bannerUIManager.displayBanner({
            text: `${patientCount} ${pluralize(patientCount, "patient has", "patients have")} been moved ${text}`,
        });
    }
    async accessDeidentifiedData(patients) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const blob = await this.fullRecordService.downloadZip(patients, study.id);
        saveAs(blob, "patients.zip");
    }
    async accessPrescreenData(patients) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        let blob;
        if (study.accessData === StudyAccessData.FULL_RECORD_DEIDENTIFIED) {
            blob = await this.fullRecordService.downloadZip(patients, study.id);
        }
        else {
            blob = await this.fullRecordService.downloadIdentifiedZip(patients, study.id);
        }
        saveAs(blob, "patients.zip");
    }
    async removeFromCohort(patients, isSelectAllBulkAction) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const filter = this.searchInput.filter;
        await Promise.all(patients.map(async (patient) => {
            let lastActivityType = null;
            if ([IdentifiedPatientState.PRESCREEN_CONSENT_PENDING, IdentifiedPatientState.PRESCREEN_CONSENTED].includes(patient.state)) {
                lastActivityType = IdentifiedPatientLastActivityType.REVOKED_PRESCREENING;
            }
            else if ([IdentifiedPatientState.ENROLLING, IdentifiedPatientState.ENROLLED].includes(patient.state)) {
                lastActivityType = IdentifiedPatientLastActivityType.REVOKED_ENROLLMENT;
            }
            await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                removed: true,
                ...(lastActivityType ? { lastActivityType } : null),
            });
        }));
        await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
        this.displayBanner(patients.length, "to Excluded");
    }
    async sendEnrollmentRequest(patients, isSelectAllBulkAction, isResend, isReengage) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const filter = this.searchInput.filter;
        try {
            this.requestConsentPendingDialogModel.numberOfPatients = patients.length;
            this.requestConsentPendingDialogModel.dataAccessType = "ENROLLMENT";
            this.requestConsentPendingDialogModel.show();
            const result = await this.studyManager.requestPatientStudyConsentForEnrollment(study, patients, isResend);
            if (!this.isDataUser && result) {
                this.requestConsentSuccessDialogModel.numberOfPatients = result.numberOfPatients;
                this.requestConsentSuccessDialogModel.errorPatients = result.errorPatients;
                this.requestConsentSuccessDialogModel.dataAccessType = "ENROLLMENT";
                await Promise.all(result.successPatients.map(async (patient) => {
                    await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                        lastActivityType: isResend
                            ? IdentifiedPatientLastActivityType.REMINDER_ENROLLMENT_REQUEST_SENT
                            : isReengage
                                ? IdentifiedPatientLastActivityType.REENGAED_ENROLLMENT_REQUEST_SENT
                                : IdentifiedPatientLastActivityType.ENROLLMENT_REQUEST_SENT,
                    });
                }));
            }
            else {
                await Promise.all(patients.map(async (patient) => {
                    await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                        lastActivityType: isResend
                            ? IdentifiedPatientLastActivityType.REMINDER_ENROLLMENT_REQUEST_SENT
                            : isReengage
                                ? IdentifiedPatientLastActivityType.REENGAED_ENROLLMENT_REQUEST_SENT
                                : IdentifiedPatientLastActivityType.ENROLLMENT_REQUEST_SENT,
                    });
                }));
            }
        }
        finally {
            this.requestConsentPendingDialogModel.open = false;
            if (!this.isDataUser) {
                const confirmed = await this.requestConsentSuccessDialogModel.showAndWait();
                await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
                if (this.requestConsentSuccessDialogModel.numberOfPatients > 0) {
                    this.displayBanner(this.requestConsentSuccessDialogModel.numberOfPatients, "to Enrolling");
                }
                if (!confirmed && this.requestConsentSuccessDialogModel.errorPatients.length > 0) {
                    const tryAgainPatients = patients.filter((patient) => this.requestConsentSuccessDialogModel.tryAgainPatients.some((errorPatient) => errorPatient.patientId === patient.patientId));
                    await this.sendEnrollmentRequest(tryAgainPatients, isSelectAllBulkAction, isResend, isReengage);
                }
            }
            else {
                await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
            }
        }
    }
    async resendEnrollmentRequest(patients, isSelectAllBulkAction) {
        await this.sendEnrollmentRequest(patients, isSelectAllBulkAction, true);
    }
    async reengageEnrollmentRequest(patients, isSelectAllBulkAction) {
        await this.sendEnrollmentRequest(patients, isSelectAllBulkAction, false, true);
    }
    async accessEnrollmentData(patients) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        let blob;
        if (study.accessData === StudyAccessData.FULL_RECORD_DEIDENTIFIED) {
            blob = await this.fullRecordService.downloadZip(patients, study.id);
        }
        else {
            blob = await this.fullRecordService.downloadIdentifiedZip(patients, study.id);
        }
        saveAs(blob, "patients.zip");
    }
    getRecruitmentStageTitle(addBackState) {
        switch (addBackState) {
            case IdentifiedPatientState.PRESCREEN_CONSENT_PENDING:
            case IdentifiedPatientState.PRESCREEN_CONSENTED:
                return "Prescreening";
            case IdentifiedPatientState.ENROLLING:
            case IdentifiedPatientState.ENROLLING_INTERESTED:
            case IdentifiedPatientState.ENROLLING_CONTACTED:
                return "Enrolling";
            case IdentifiedPatientState.ENROLLED:
                return "Enrolled";
            case IdentifiedPatientState.DECLINED_PRESCREEN:
            case IdentifiedPatientState.DECLINED_ENROLLMENT:
            case IdentifiedPatientState.REVOKED_PRESCREEN:
            case IdentifiedPatientState.REVOKED_ENROLLMENT:
            case IdentifiedPatientState.INFORMATION_SENT:
            case IdentifiedPatientState.REMOVED:
                return null;
        }
        return willNeverHappen(addBackState);
    }
    async addBackToCohort(patients, isSelectAllBulkAction) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const filter = this.searchInput.filter;
        await Promise.all(patients.map(async (patient) => {
            await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                removed: false,
            });
        }));
        await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
        const uniqueAddBackStates = uniqueArray(patients.map((patient) => patient.addBackState));
        if (uniqueAddBackStates.length > 1) {
            this.displayBanner(patients.length, "out of Excluded");
        }
        else {
            const recruitmentStage = this.getRecruitmentStageTitle(uniqueAddBackStates[0]);
            if (recruitmentStage)
                this.displayBanner(patients.length, `to ${recruitmentStage}`);
        }
    }
    async updateHealthRecords(patients) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        await this.studyManager.updateHealthRecords(study, patients);
        this.bannerUIManager.displayBanner({
            text: `Health record update request sent to ${pluralize(patients.length, "patient", "patients")}`,
        });
    }
    async sendStudyInformation(patients) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        await this.studyManager.sendStudyInformation(study, patients);
        this.bannerUIManager.displayBanner({ text: `Study information sent to ${pluralize(patients.length, "patient", "patients")}` });
    }
    async markAsContacted(patients, isSelectAllBulkAction) {
        const study = this.studyToManage;
        if (study === NOT_LOADED || study === null)
            return;
        if (this.isStudyPaused)
            return;
        const filter = this.searchInput.filter;
        await Promise.all(patients.map(async (patient) => {
            await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(study.id, patient.organizationId, patient.patientId, {
                contacted: true,
                lastActivityType: IdentifiedPatientLastActivityType.CONTACTED_BY_RESEARCHER,
            });
        }));
        await this.reloadPatientsAfterAction(filter, isSelectAllBulkAction);
        this.bannerUIManager.displayBanner({
            text: `Marked ${pluralize(patients.length, "patient", "patients")} as contacted`,
        });
    }
    getSelectedPatientIds() {
        var _a, _b;
        if (this.selectAll) {
            return (_a = this.selectAllSelectedPatientsIds) !== null && _a !== void 0 ? _a : undefined;
        }
        return (_b = this.allSelectedPatientIds) !== null && _b !== void 0 ? _b : undefined;
    }
    async updatePagination(paginationParams) {
        const filter = this.searchInput.filter;
        this.paginationState = paginationParams;
        await this.loadPatients(filter);
    }
    getPatientError(error) {
        switch (error) {
            case RequestConsentError.ALREADY_OPTED_IN:
                return "this patient has already received this request. No further action is needed.";
            case RequestConsentError.INTERNAL_ERROR:
                return "an internal error occurred. Please try again.";
        }
        return willNeverHappen(error);
    }
    getPatientRequestErrorInfo(patient) {
        return [patient.mrn, patient.patientId, this.getPatientError(patient.error)]
            .filter((value) => !!value)
            .map((value) => value)
            .join(", ");
    }
    setSelectedPatientIdsFromTable(selectedPatientIds) {
        const filter = this.searchInput.filter;
        if (this.selectAll) {
            this.selectAll = false;
        }
        this.updateSelectedPatientIds(filter, selectedPatientIds);
    }
    updateSelectedPatientIds(filter, selectedPatientIds) {
        var _a, _b;
        this.allSelectedPatientIds = selectedPatientIds;
        const allSelectedPatients = ((_a = this.allSelectedPatients) !== null && _a !== void 0 ? _a : []).filter((patient) => !this.identifiedPatientIds.has(patient.patientId));
        const selectedPatientIdsSet = new Set(selectedPatientIds);
        const identifiedPatients = (_b = this.identifiedPatients) !== null && _b !== void 0 ? _b : [];
        identifiedPatients.forEach((patient) => {
            if (selectedPatientIdsSet.has(patient.patientId))
                allSelectedPatients.push(patient);
        });
        this.allSelectedPatients = allSelectedPatients;
        this.perPageSelectedPatientIds.set(this.perPageKey, selectedPatientIds.filter((selectedPatientId) => this.identifiedPatientIds.has(selectedPatientId)));
    }
    get areAllPatientsSelectedForPage() {
        var _a, _b;
        return ((_b = (_a = this.perPageSelectedPatientIds.get(this.perPageKey)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) >= this.identifiedPatientIds.size;
    }
    get perPageKey() {
        return `${this.searchInput.filter}_${this.currentPaginationPage}`;
    }
    get currentPaginationPage() {
        var _a;
        const paginationInput = this.paginationState;
        return (_a = paginationInput === null || paginationInput === void 0 ? void 0 : paginationInput.page) !== null && _a !== void 0 ? _a : 0;
    }
    get showSelectAllBannerType() {
        if (this.totalIdentifiedPatients <= defaultPageSize)
            return null;
        if (this.areAllPatientsSelectedForPage && !this.selectAll) {
            return RecruitmentSelectAllBannerType.SELECT_ALL;
        }
        else if (this.selectAll) {
            return RecruitmentSelectAllBannerType.CLEAR_ALL;
        }
        return null;
    }
    getFilterDescription(filter) {
        if (filter === "ALL")
            return "All";
        return this.getStatusDescription(filter);
    }
    get currentFilterText() {
        return this.getFilterDescription(this.searchInput.filter);
    }
    async loadSelectAllPatients(filter) {
        var _a, _b;
        if (this.studyToManage === NOT_LOADED) {
            this.identifiedPatients = null;
            return;
        }
        if (this.studyToManage === null) {
            this.identifiedPatients = [];
            return;
        }
        const { patientInfo: patientId } = this.searchInput;
        const searchInput = {
            patientInfo: patientId,
            state: filter === "ALL" ? undefined : [filter],
            showPatientRequestConsentErrors: true,
        };
        const selectAllIdentifiedPatients = (_b = (_a = (await this.studyRecruitmentManager.getIdentifiedPatients(this.studyToManage.id, searchInput))) === null || _a === void 0 ? void 0 : _a.patients) !== null && _b !== void 0 ? _b : [];
        this.selectAllIdentifiedPatients = selectAllIdentifiedPatients;
        const selectAllSelectedPatientsIds = selectAllIdentifiedPatients.map((patient) => patient.patientId);
        this.selectAllSelectedPatientsIds = selectAllSelectedPatientsIds;
    }
    async reloadPatientsAfterAction(filter, isSelectAllBulkAction) {
        await this.loadPatientSummary();
        await this.loadPatients(filter);
        if (isSelectAllBulkAction) {
            this.clearSelectAll(filter);
        }
        else {
            if (this.selectAll)
                await this.loadSelectAllPatients(filter);
        }
    }
    clearSelectAll(filter) {
        this.selectAll = false;
        this.selectAllIdentifiedPatients = [];
        this.selectAllSelectedPatientsIds = [];
        this.allSelectedPatients = [];
        this.allSelectedPatientIds = [];
        Array.from(this.perPageSelectedPatientIds.keys()).forEach((key) => {
            if (key.startsWith(filter))
                this.perPageSelectedPatientIds.set(key, []);
        });
    }
    async onSelectAll() {
        try {
            await this.loadSelectAllPatients(this.searchInput.filter);
        }
        finally {
            this.selectAll = true;
        }
    }
    onClearAll() {
        this.clearSelectAll(this.searchInput.filter);
    }
    get manageRemindersDialogText() {
        if (this.studyToManage === NOT_LOADED)
            return "";
        return "Set up automated reminders for patients who have been sent a request. These reminders will be sent at the chosen frequency until the patient responds or the maximum number of reminders is reached. Changes here apply to all new and existing requests.";
    }
    async manageReminders() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return;
        const studyReminder = await this.studyManager.getStudyReminder(this.studyToManage.id);
        if (studyReminder) {
            const { enrollmentEnabled, enrollmentEvery, enrollmentUnit, enrollmentLimit, prescreenEnabled, prescreenEvery, prescreenUnit, prescreenLimit, } = studyReminder;
            this.manageRemindersDialogFormModel.updateInput({
                enrollmentEnabled,
                enrollmentEvery,
                enrollmentUnit,
                enrollmentLimit,
                prescreenEnabled,
                prescreenEvery,
                prescreenUnit,
                prescreenLimit,
            });
        }
        else {
            this.manageRemindersDialogFormModel.updateInput(this.studyReminderDefaults);
        }
        this.manageRemindersDialogModel.show();
    }
    get isDataUser() {
        return this.studyRecruitmentManager.isDataUser;
    }
    showAddPatientsWizardDialog() {
        this.dataRequestorAddPatientsDialogModel.show();
    }
    showDataOriginatorAddPatientsDialog() {
        this.dataOriginatorAddPatientsDialogModel.show();
    }
    async closeDataOriginatorAddPatientsDialog() {
        this.dataOriginatorAddPatientsDialogModel.open = false;
        await this.reloadPatientsAfterAction(this.searchInput.filter);
    }
    async showRevocationDetailsDialog(patient) {
        this.selectedPatientForRevocationDetailsDialog = patient;
        this.revocationDetailsDialogModel.show();
    }
    closeRevocationDetailsDialog() {
        this.revocationDetailsDialogModel.open = false;
        this.selectedPatientForRevocationDetailsDialog = null;
    }
    validateManageRemindersForm() {
        this.manageRemindersDialogFormModel.clearErrors();
        if (this.manageRemindersDialogFormModel.input.enrollmentEnabled) {
            this.manageRemindersDialogFormModel.validateField("enrollmentEvery", validators.notNull, "Enter a value");
            this.manageRemindersDialogFormModel.validateField("enrollmentEvery", validators.positiveNumber, "Must be at least 1");
            this.manageRemindersDialogFormModel.validateField("enrollmentLimit", validators.notNull, "Enter a value");
            this.manageRemindersDialogFormModel.validateField("enrollmentLimit", validators.positiveNumber, "Must be at least 1");
        }
        if (this.manageRemindersDialogFormModel.input.prescreenEnabled) {
            this.manageRemindersDialogFormModel.validateField("prescreenEvery", validators.notNull, "Enter a value");
            this.manageRemindersDialogFormModel.validateField("prescreenEvery", validators.positiveNumber, "Must be at least 1");
            this.manageRemindersDialogFormModel.validateField("prescreenLimit", validators.notNull, "Enter a value");
            this.manageRemindersDialogFormModel.validateField("prescreenLimit", validators.positiveNumber, "Must be at least 1");
        }
    }
    get saveRemindersEnabled() {
        return !this.manageRemindersDialogFormModel.hasErrors();
    }
    async saveReminders() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return;
        const { enrollmentEnabled, enrollmentEvery, enrollmentUnit, enrollmentLimit, prescreenEnabled, prescreenEvery, prescreenUnit, prescreenLimit, } = this.manageRemindersDialogFormModel.input;
        await this.studyManager.upsertStudyReminder(this.studyToManage.id, {
            enrollmentEnabled,
            enrollmentEvery: enrollmentEvery !== null && enrollmentEvery !== void 0 ? enrollmentEvery : this.studyReminderDefaults.enrollmentEvery,
            enrollmentUnit,
            enrollmentLimit: enrollmentLimit !== null && enrollmentLimit !== void 0 ? enrollmentLimit : this.studyReminderDefaults.enrollmentLimit,
            prescreenEnabled,
            prescreenEvery: prescreenEvery !== null && prescreenEvery !== void 0 ? prescreenEvery : this.studyReminderDefaults.prescreenEvery,
            prescreenUnit,
            prescreenLimit: prescreenLimit !== null && prescreenLimit !== void 0 ? prescreenLimit : this.studyReminderDefaults.prescreenLimit,
        });
        this.manageRemindersDialogModel.onChoice(false);
        this.bannerUIManager.displayBanner({ text: "Reminders successfully saved" });
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "studyVersions", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "identifiedPatientSummary", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "identifiedPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "identifiedPatientIds", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "searchInput", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "allSelectedPatientIds", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "allSelectedPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "sort", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], RecruitmentViewModel.prototype, "totalIdentifiedPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", ApiPaginationInput)
], RecruitmentViewModel.prototype, "paginationState", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "identifiedPatientsResponse", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "selectedPatientForRevocationDetailsDialog", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "studyToManage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "isStudyPaused", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "isGeneralDataUse", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showCheckboxSelection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showStudyVersions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showPatientActions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showBulkActions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showAddPatientsButton", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showManagerRemindersButton", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "readonly", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "isPdda", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "noPatientInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "filters", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "noPatientsFoundWithSearch", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "selectedPatients", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "searchPlaceholderText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showMRN", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showNameAndContactInformation", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "isStudyUsingIdentifiedData", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "bulkActionButtonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "bulkActions", null);
__decorate([
    observable,
    __metadata("design:type", Map)
], RecruitmentViewModel.prototype, "perPageSelectedPatientIds", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], RecruitmentViewModel.prototype, "selectAll", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "selectAllIdentifiedPatients", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], RecruitmentViewModel.prototype, "selectAllSelectedPatientsIds", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], RecruitmentViewModel.prototype, "numberOfCurrentPageIdentifiedPatients", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "areAllPatientsSelectedForPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "perPageKey", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "currentPaginationPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "showSelectAllBannerType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "currentFilterText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "manageRemindersDialogText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "isDataUser", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], RecruitmentViewModel.prototype, "saveRemindersEnabled", null);
